import { useEffect, useRef } from 'react'
import { Form } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { Logo } from '#app/components/ui/logo.tsx'

type CallbackData = Record<string, string | undefined>

export function TilopayPaymentConfirmation({
	autoSubmit = true,
	callbackData,
	detail,
}: {
	autoSubmit?: boolean
	callbackData: CallbackData
	detail: string
}) {
	const formRef = useRef<HTMLFormElement>(null)
	const submittedRef = useRef(false)

	useEffect(() => {
		if (!autoSubmit || submittedRef.current) return
		submittedRef.current = true
		formRef.current?.requestSubmit()
	}, [autoSubmit])

	return (
		<main className="relative isolate flex min-h-[100dvh] flex-col overflow-hidden bg-[#f7f8fb] text-[#15171d] dark:bg-[#10131c] dark:text-[#f4f6fb]">
			<div
				aria-hidden
				className="absolute inset-x-0 top-[42%] -z-10 h-px bg-[#dde2ea] dark:bg-[#282e3d]"
			/>
			<div
				aria-hidden
				className="absolute -left-24 top-[42%] -z-10 h-48 w-48 rounded-full bg-[#e5ecf6] dark:bg-[#171d2a]"
			/>

			<header className="flex items-center justify-between px-5 py-5 sm:px-8 sm:py-7">
				<div className="flex items-center gap-2.5">
					<Logo className="h-8 w-8 shadow-sm" />
					<span className="text-sm font-semibold tracking-[0.12em] text-[#20242d] dark:text-[#f4f6fb]">IUS PRO</span>
				</div>
				<div className="flex items-center gap-2 text-xs font-medium text-[#596273] dark:text-[#aeb7c8]">
					<Icon name="lock-closed" className="h-3.5 w-3.5" aria-hidden />
					Pago seguro
				</div>
			</header>

			<section
				role="status"
				aria-live="polite"
				aria-busy="true"
				className="mx-auto flex w-full max-w-[38rem] flex-1 flex-col justify-center px-6 pb-24 pt-12 sm:px-10"
			>
				<div className="mb-10 flex items-center gap-6 sm:mb-12 sm:gap-8">
					<div className="relative h-24 w-24 shrink-0 sm:h-28 sm:w-28" aria-hidden>
						<div className="absolute inset-0 rounded-full border border-[#d9dee8] dark:border-[#303749]" />
						<div className="absolute inset-0 animate-[spin_1.6s_linear_infinite] rounded-full border-2 border-transparent border-r-[#24578f]/30 border-t-[#24578f] motion-reduce:animate-none dark:border-r-[#77a7d7]/30 dark:border-t-[#77a7d7]" />
						<div className="absolute inset-3 flex items-center justify-center rounded-full bg-[#eef2f8] text-[#24578f] dark:bg-[#1b2230] dark:text-[#8ab4df]">
							<Icon name="credit-card" className="h-8 w-8 sm:h-9 sm:w-9" />
						</div>
					</div>
					<div className="h-px flex-1 overflow-hidden bg-[#d9dee8] dark:bg-[#303749]" aria-hidden>
						<div className="h-full w-2/3 animate-pulse bg-[#24578f] motion-reduce:animate-none dark:bg-[#77a7d7]" />
					</div>
				</div>

				<p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#24578f] dark:text-[#8ab4df]">
					Verificación en curso
				</p>
				<h1 className="max-w-[15ch] text-3xl font-semibold leading-[1.12] tracking-[-0.035em] text-[#171a21] dark:text-[#f7f8fb] sm:text-5xl">
					Estamos confirmando tu pago
				</h1>
				<p className="mt-5 max-w-[52ch] text-base leading-7 text-[#596273] dark:text-[#aeb7c8]">{detail}</p>

				<div className="mt-9 flex items-start gap-3 border-t border-[#dde2ea] pt-5 text-sm leading-6 text-[#6a7384] dark:border-[#282e3d] dark:text-[#97a1b4]">
					<Icon name="shield" className="mt-0.5 h-4 w-4 shrink-0 text-[#24578f] dark:text-[#8ab4df]" aria-hidden />
					<p>No cierres esta ventana. Te llevaremos a IUS PRO apenas tus recursos estén listos.</p>
				</div>

				{autoSubmit ? (
					<Form ref={formRef} method="post" replace>
						{Object.entries(callbackData).map(([name, value]) =>
							value === undefined ? null : <input key={name} type="hidden" name={name} value={value} />,
						)}
						<button type="submit" className="sr-only" tabIndex={-1}>
							Confirmar pago
						</button>
						<noscript>
							<div className="mt-7 rounded-lg border border-[#ccd4e0] bg-[#eef2f8] p-4 text-sm text-[#303744] dark:border-[#374055] dark:bg-[#1b2230] dark:text-[#d7deea]">
								JavaScript está desactivado. Presiona “Confirmar pago” para continuar.
								<button
									type="submit"
									className="mt-3 min-h-11 w-full rounded-md bg-[#24578f] px-4 py-2.5 font-semibold text-white"
								>
									Confirmar pago
								</button>
							</div>
						</noscript>
					</Form>
				) : null}
			</section>
		</main>
	)
}
